import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Wallet,
  Calendar,
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  FileText,
  DollarSign,
  AlertCircle
} from "lucide-react";
import { Table, DatePicker, Tag, Card, Statistic, Row, Col } from "antd";
import dayjs from "dayjs";

const AdminCelestialLedger = () => {
  const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "";
  
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  
  const [statement, setStatement] = useState([]);
  const [loadingStatement, setLoadingStatement] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(dayjs());

  useEffect(() => {
    fetchBalance();
    // Default fetch statement for today
    fetchStatement(dayjs().format("YYYY-MM-DD"));
  }, []);

  const fetchBalance = async () => {
    setLoadingBalance(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/balance`,
        {},
        { withCredentials: true }
      );
      if (res.data?.success && res.data?.data?.balance) {
        setBalance(res.data.data.balance);
      } else {
        toast.error(res.data?.message || "Failed to fetch balance");
      }
    } catch (error) {
      console.error("Balance fetch error:", error);
      toast.error(error.response?.data?.message || "Failed to load balance");
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchStatement = async (dateStr) => {
    setLoadingStatement(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/statement`,
        { date: dateStr },
        { withCredentials: true }
      );
      
      if (res.data?.success) {
        // The API returns an object or array in data.data or data.data.data
        const stData = res.data.data?.data || res.data.data || [];
        setStatement(Array.isArray(stData) ? stData : []);
      } else {
        toast.error(res.data?.message || "Failed to fetch statement");
      }
    } catch (error) {
      console.error("Statement fetch error:", error);
      toast.error(error.response?.data?.message || "Failed to load statement");
    } finally {
      setLoadingStatement(false);
    }
  };

  const handleDateChange = (date, dateString) => {
    setSelectedDate(date);
    if (dateString) {
      fetchStatement(dateString);
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    return isNaN(num) ? "₹0.00" : `₹${num.toLocaleString('en-IN')}`;
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "s_no",
      key: "s_no",
      width: 80,
    },
    {
      title: "Date",
      dataIndex: "value_date",
      key: "value_date",
      width: 180,
    },
    {
      title: "Transaction Ref",
      dataIndex: "transaction_ref",
      key: "transaction_ref",
      render: (text) => <span className="font-mono text-xs">{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "transaction_type",
      key: "transaction_type",
      render: (type) => {
        let color = "blue";
        if (type?.toLowerCase().includes("refund")) color = "green";
        if (type?.toLowerCase().includes("booking")) color = "orange";
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      render: (val) => (
        <span className={val > 0 ? "text-red-600 font-semibold" : ""}>
          {val > 0 ? `-${formatCurrency(val)}` : "-"}
        </span>
      ),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      render: (val) => (
        <span className={val > 0 ? "text-green-600 font-semibold" : ""}>
          {val > 0 ? `+${formatCurrency(val)}` : "-"}
        </span>
      ),
    },
    {
      title: "TDS / Fees",
      key: "fees",
      render: (_, record) => {
        const fees = Number(record.txn_fees || 0) + Number(record.payment_charge || 0);
        return (
          <div className="text-xs text-gray-500">
            <div>TDS: {formatCurrency(record.tds)}</div>
            <div>Fees: {formatCurrency(fees)}</div>
          </div>
        );
      },
    },
    {
      title: "Comm. / Markup",
      key: "comm_markup",
      render: (_, record) => (
        <div className="text-xs text-gray-500">
          <div>Comm: <span className="text-green-600">{formatCurrency(record.commission || record.Commission)}</span></div>
          <div>Markup: {formatCurrency(record.markup)}</div>
        </div>
      ),
    },
    {
      title: "Closing Balance",
      dataIndex: "booking_balance",
      key: "booking_balance",
      render: (val) => <span className="font-bold">{formatCurrency(val)}</span>,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <Wallet className="w-6 h-6 mr-2 text-blue-600" />
            Celestial Ledger
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your API provider wallet balance and view daily transaction statements.
          </p>
        </div>
        <button
          onClick={fetchBalance}
          disabled={loadingBalance}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium border border-blue-200"
        >
          <RefreshCw className={`w-4 h-4 ${loadingBalance ? 'animate-spin' : ''}`} />
          Refresh Balance
        </button>
      </div>

      {/* Balance Widget */}
      <Row gutter={[16, 16]} className="mb-8">
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} className="shadow-sm border border-gray-100 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-blue-100 font-medium flex items-center">
                <DollarSign className="w-4 h-4 mr-1" /> Available Balance
              </h3>
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Wallet className="w-5 h-5 text-white" />
              </div>
            </div>
            {loadingBalance ? (
              <div className="animate-pulse flex space-x-4">
                <div className="h-8 bg-white/30 rounded w-1/2"></div>
              </div>
            ) : (
              <div>
                <h2 className="text-3xl font-bold tracking-tight">
                  {formatCurrency(balance)}
                </h2>
                <p className="text-blue-100 text-xs mt-2 opacity-80">
                  Real-time balance from API Provider
                </p>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Statement Section */}
      <Card 
        bordered={false} 
        className="shadow-sm border border-gray-100 rounded-xl"
        title={
          <div className="flex items-center text-gray-700">
            <FileText className="w-5 h-5 mr-2 text-blue-500" />
            Transaction Statement
          </div>
        }
        extra={
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 font-medium">Select Date:</span>
            <DatePicker 
              value={selectedDate} 
              onChange={handleDateChange} 
              format="YYYY-MM-DD"
              allowClear={false}
              className="rounded-lg shadow-sm border-gray-300"
            />
          </div>
        }
      >
        {statement.length === 0 && !loadingStatement ? (
          <div className="py-12 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-800">No Transactions Found</h3>
            <p className="text-gray-500 mt-1 max-w-sm">
              There are no transactions available for {selectedDate?.format("DD MMM, YYYY")}. Try selecting a different date.
            </p>
          </div>
        ) : (
          <Table 
            columns={columns} 
            dataSource={statement} 
            rowKey={(record) => record.s_no || record.transaction_ref || Math.random().toString()} 
            loading={loadingStatement}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
            className="celestial-table"
          />
        )}
      </Card>
    </div>
  );
};

export default AdminCelestialLedger;
